import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, timer } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

const BASE = `${environment.apiUrl}/ai-integrations`;

/** Blocos do metadata de um serviço que o painel de administração edita. */
export interface AiServiceMetadata {
  pricing?: { [key: string]: number };
  models?: { [key: string]: string };
  limits?: { [key: string]: number };
}

export interface AiWallet {
  id: number;
  scope: 'ENTITY' | 'STORE' | 'USER';
  entityId: number;
  storeId?: number | null;
  userId?: string | null;
  balance: number;
  reservedBalance: number;
  shareWithSubordinates: boolean;
  store?: { id: number; name: string } | null;
  user?: { id: string; name?: string; email?: string } | null;
  createdAt: string;
}

export interface AiCreditRequest {
  id: number;
  entityId: number;
  entity?: { id: number; name: string } | null;
  storeId?: number | null;
  targetScope: 'ENTITY' | 'STORE' | 'USER';
  targetUserId?: string | null;
  requestedById: string;
  creditsRequested: number;
  reason?: string | null;
  status: string;
  emailSentAt?: string | null;
  decidedAt?: string | null;
  decisionNote?: string | null;
  requestedBy?: { id: string; name?: string; email?: string };
  decidedBy?: { id: string; name?: string; email?: string } | null;
  store?: { id: number; name: string } | null;
  targetUser?: { id: string; name?: string; email?: string } | null;
  billingRecipientType?: 'ENTITY' | 'STORE' | 'USER' | null;
  billingName?: string | null;
  billingFiscalIdentification?: string | null;
  billingAddress?: string | null;
  billingEmail?: string | null;
  billingPhone?: string | null;
  origin?: 'CRM' | 'PARTNER_API' | 'ADMIN';
  partnerClientId?: number | null;
  partnerClient?: { id: number; clientId: string; name: string } | null;
  externalReference?: string | null;
  walletId?: number | null;
  createdAt: string;
}

/** Filtros da vista global de pedidos de crédito (Admin IA). Datas em YYYY-MM-DD. */
export interface AiCreditRequestFilters {
  status?: string | null;
  origin?: string | null;
  entityId?: number | null;
  storeId?: number | null;
  from?: string | null;
  to?: string | null;
}

export interface AdminOption {
  id: number;
  name: string;
}

export interface AdminUserOption {
  id: string;
  name?: string | null;
  email?: string | null;
}

export interface AiAdminGrantStorePreview {
  id: number;
  name: string;
  walletId: number | null;
  balance: number;
  reservedBalance: number;
}

export interface AiAdminGrantPreview {
  walletId: number | null;
  balance: number;
  reservedBalance: number;
  lastGrantAt: string | null;
  lastGrantAmount: number | null;
  lastGrantBy: string | null;
  lastGrantNote: string | null;
  /** Só no âmbito ALL_STORES: uma linha por agência ativa. */
  stores?: AiAdminGrantStorePreview[];
}

export interface AiAdminGrantResult {
  id?: number;
  balance?: number;
  before: number;
  after: number;
  creditsGranted: number;
  allStores?: boolean;
  storesCount?: number;
  creditsTotal?: number;
  stores?: { storeId: number; storeName: string; walletId: number; before: number; after: number }[];
}

export type AiAdminGrantScope = 'ENTITY' | 'STORE' | 'USER' | 'ALL_STORES';

export interface AiUsageLog {
  id: number;
  idempotencyKey: string;
  aiServiceId: number;
  aiService?: { id: number; code: string; name: string };
  entityId: number;
  userId: string;
  user?: { id: string; name?: string; email?: string };
  propertyId?: string | null;
  property?: { id: string; reference?: string } | null;
  creditsConsumed: number;
  status: 'SUCCESS' | 'ERROR';
  outputSummary?: string | null;
  errorCode?: string | null;
  errorMessage?: string | null;
  durationMs?: number | null;
  createdAt: string;
}

export interface AiUsageResult {
  ok: boolean;
  status?: 'PENDING' | 'SUCCESS' | 'ERROR';
  cached?: boolean;
  idempotencyKey?: string;
  output?: Record<string, any>;
  creditsConsumed?: number;
  creditsReserved?: number;
  remainingBalance?: number;
  errorCode?: string;
  errorMessage?: string;
}

export interface AiUsageLookupResult extends AiUsageResult {
  status: 'PENDING' | 'SUCCESS' | 'ERROR';
}

export interface HomeStagingAiRequest {
  idempotencyKey: string;
  image: File | Blob;
  imageName?: string;
  propertyId?: string;
  roomType?: string;
  style?: string;
  quality?: 'auto' | 'low' | 'medium' | 'high';
  variants?: number;
  customInstructions?: string;
  imageCaption?: string;
  propertyContext?: Record<string, any>;
}

export interface ImageEnhancementAiRequest {
  idempotencyKey: string;
  image: File | Blob;
  imageName?: string;
  propertyId?: string;
  enhancements?: string[];
  quality?: 'auto' | 'low' | 'medium' | 'high';
  customInstructions?: string;
  imageCaption?: string;
  propertyContext?: Record<string, any>;
}

export interface AiCreditPolicyOverview {
  entity: {
    id: number;
    storeCanRequestOwnCredits: boolean;
    userCanRequestOwnCredits: boolean;
    creditRequestFlow: 'MASTER_APPROVAL_REQUIRED' | 'DIRECT_TO_IMODIGI';
    invoiceRecipientForStoreRequests: 'ENTITY' | 'STORE';
    invoiceRecipientForUserRequests: 'ENTITY' | 'STORE' | 'USER';
    updatedAt: string;
  };
  stores: Array<{
    storeId: number;
    storeName: string;
    policy: {
      id: number;
      userCanRequestOwnCredits: boolean;
      invoiceRecipientForUserRequests?: 'ENTITY' | 'STORE' | 'USER' | null;
      updatedAt: string;
    } | null;
    wallet: { id: number; balance: number; reservedBalance: number; shareWithSubordinates: boolean } | null;
  }>;
}

export interface AiDistributionSource {
  id: number;
  scope: 'ENTITY' | 'STORE' | 'USER';
  label: string;
  balance: number;
  reservedBalance: number;
  available: number;
  storeId?: number | null;
}

export interface AiDistributionTarget {
  targetType: 'STORE' | 'USER';
  targetId: number | string;
  name: string;
  email?: string;
  subtitle?: string;
  walletId: number | null;
  balance: number;
  reservedBalance: number;
  available: number;
}

export interface AiBillingConfig {
  pricePerCredit: number;
  currencyCode: string;
  currencySymbol: string;
  updatedAt?: string | null;
}

/** Preço de um modelo de IA (custo real para a Imodigi), mantido no AI Admin. */
export interface AiModelPrice {
  id: number;
  model: string;
  providerCode?: string | null;
  inputPerMillion?: number | string | null;
  cachedInputPerMillion?: number | string | null;
  outputPerMillion?: number | string | null;
  perImage?: number | string | null;
  currencyCode: string;
  status: boolean;
  note?: string | null;
  updatedAt?: string;
}

export interface AiCostReportFilters {
  from?: string | null;
  to?: string | null;
  entityId?: number | null;
  aiServiceId?: number | null;
  groupBy?: 'service' | 'entity';
}

export interface AiCostReportRow {
  key: number;
  label: string;
  code?: string | null;
  usages: number;
  credits: number;
  revenueEur: number;
  costEur: number;
  marginEur: number;
  marginPct: number | null;
  unpricedCalls: number;
}

export interface AiCostUsageItem {
  id: number;
  idempotencyKey: string;
  createdAt: string;
  status: string;
  service?: { id: number; code: string; name: string } | null;
  entity?: { id: number; name: string } | null;
  user?: { id: string; name?: string | null; email?: string | null } | null;
  propertyId?: string | null;
  credits: number;
  revenueEur: number | null;
  costEur: number | null;
  marginEur: number | null;
  calls: number;
  unpricedCalls: number;
  models: string[];
  tokensIn: number;
  tokensOut: number;
  units: number;
  durationMs?: number | null;
}

export interface AiCostUsagesPage {
  page: number;
  pageSize: number;
  total: number;
  items: AiCostUsageItem[];
}

export interface AiCostReport {
  groupBy: 'service' | 'entity';
  rows: AiCostReportRow[];
  totals: AiCostReportRow;
  unpricedModels: { model: string; calls: number }[];
  pricePerCredit: number;
  usdPerEur: number | null;
  currencyCode: string;
}

export interface AiServicePool {
  id: number;
  aiServiceId: number;
  serviceCode: string;
  serviceName: string;
  balance: number;
  reservedBalance: number;
  available: number;
}

export interface BackgroundRemovalRequest {
  idempotencyKey: string;
  image: File | Blob;
  imageName?: string;
}

export interface AiCreditRequestContext {
  creditRequestFlow: 'MASTER_APPROVAL_REQUIRED' | 'DIRECT_TO_IMODIGI';
  requesterType: 'MASTER' | 'STORE' | 'USER';
  invoiceRecipientForStoreRequests: 'ENTITY' | 'STORE';
  invoiceRecipientForUserRequests: 'ENTITY' | 'STORE' | 'USER';
  storeUserInvoiceRecipientOverride?: 'ENTITY' | 'STORE' | 'USER' | null;
  effectiveInvoiceRecipientType: 'ENTITY' | 'STORE' | 'USER';
  canRequestCredits: boolean;
  requiresBillingData: boolean;
  canConfigureStoreUserInvoiceRecipient: boolean;
  primaryStoreId?: number | null;
  billingPrefill: {
    billingName: string;
    billingFiscalIdentification: string;
    billingAddress: string;
    billingEmail: string;
    billingPhone: string;
  };
}

@Injectable({ providedIn: 'root' })
export class AiIntegrationsService {
  constructor(private http: HttpClient) {}

  // ── Wallets ──────────────────────────────────────────────────────────────
  getWallets(): Observable<AiWallet[]> {
    return this.http.get<AiWallet[]>(`${BASE}/wallets`, { withCredentials: true });
  }

  createWallet(scope: string, storeId?: number, userId?: string): Observable<AiWallet> {
    return this.http.post<AiWallet>(`${BASE}/wallets`, { scope, storeId, userId }, { withCredentials: true });
  }

  updateShare(walletId: number, shareWithSubordinates: boolean): Observable<AiWallet> {
    return this.http.patch<AiWallet>(`${BASE}/wallets/${walletId}/share`, { shareWithSubordinates }, { withCredentials: true });
  }

  grantCredits(walletId: number, amount: number, description?: string): Observable<AiWallet> {
    return this.http.post<AiWallet>(`${BASE}/wallets/grant`, { walletId, amount, description }, { withCredentials: true });
  }

  allocateCredits(sourceWalletId: number, destWalletId: number, amount: number, note?: string): Observable<any> {
    return this.http.post<any>(`${BASE}/wallets/allocate`, { sourceWalletId, destWalletId, amount, note }, { withCredentials: true });
  }

  getDistributionSources(): Observable<AiDistributionSource[]> {
    return this.http.get<AiDistributionSource[]>(`${BASE}/wallets/distribution/sources`, { withCredentials: true });
  }

  getDistributionTargets(params: {
    scope: 'STORE' | 'USER';
    search?: string;
    page?: number;
    pageSize?: number;
    sourceWalletId?: number;
  }): Observable<{ items: AiDistributionTarget[]; total: number; page: number; pageSize: number }> {
    let httpParams = new HttpParams()
      .set('scope', params.scope)
      .set('page', params.page ?? 1)
      .set('pageSize', params.pageSize ?? 20);
    if (params.search?.trim()) httpParams = httpParams.set('search', params.search.trim());
    if (params.sourceWalletId) httpParams = httpParams.set('sourceWalletId', params.sourceWalletId);
    return this.http.get<any>(`${BASE}/wallets/distribution/targets`, { params: httpParams, withCredentials: true });
  }

  transferCredits(data: {
    sourceWalletId: number;
    targetType: 'STORE' | 'USER';
    targetId: number | string;
    amount: number;
    note?: string;
  }): Observable<any> {
    return this.http.post<any>(`${BASE}/wallets/distribution/transfer`, data, { withCredentials: true });
  }

  getTransactions(walletId: number, page = 1, pageSize = 20): Observable<any> {
    const params = new HttpParams().set('page', page).set('pageSize', pageSize);
    return this.http.get<any>(`${BASE}/wallets/${walletId}/transactions`, { params, withCredentials: true });
  }

  // ── Credit Requests ───────────────────────────────────────────────────────
  getCreditRequests(page = 1, pageSize = 10, status?: string): Observable<{ items: AiCreditRequest[]; total: number }> {
    let params = new HttpParams().set('page', page).set('pageSize', pageSize);
    if (status) params = params.set('status', status);
    return this.http.get<any>(`${BASE}/credit-requests`, { params, withCredentials: true });
  }

  createCreditRequest(data: {
    /** Optional: the backend derives the effective scope from the requester role. */
    targetScope?: string;
    storeId?: number;
    targetUserId?: string;
    creditsRequested: number;
    reason?: string;
    billingName?: string;
    billingFiscalIdentification?: string;
    billingAddress?: string;
    billingEmail?: string;
    billingPhone?: string;
  }): Observable<AiCreditRequest> {
    return this.http.post<AiCreditRequest>(`${BASE}/credit-requests`, data, { withCredentials: true });
  }

  getCreditRequestContext(): Observable<AiCreditRequestContext> {
    return this.http.get<AiCreditRequestContext>(`${BASE}/credit-requests/context`, { withCredentials: true });
  }

  decideCreditRequest(id: number, status: string, decisionNote?: string): Observable<AiCreditRequest> {
    return this.http.patch<AiCreditRequest>(`${BASE}/credit-requests/${id}/decide`, { status, decisionNote }, { withCredentials: true });
  }

  /** Returns requests pending the current user's approval (STORE OPERATOR / MASTER). */
  getPendingApprovals(): Observable<AiCreditRequest[]> {
    return this.http.get<AiCreditRequest[]>(`${BASE}/credit-requests/pending`, { withCredentials: true });
  }

  approveCreditRequest(
    id: number,
    data?: {
      note?: string;
      billingName?: string;
      billingFiscalIdentification?: string;
      billingAddress?: string;
      billingEmail?: string;
      billingPhone?: string;
    },
  ): Observable<AiCreditRequest> {
    return this.http.patch<AiCreditRequest>(
      `${BASE}/credit-requests/${id}/approve`,
      data ?? {},
      { withCredentials: true },
    );
  }

  rejectCreditRequest(id: number, note?: string): Observable<AiCreditRequest> {
    return this.http.patch<AiCreditRequest>(`${BASE}/credit-requests/${id}/reject`, { note }, { withCredentials: true });
  }

  // ── Usage ─────────────────────────────────────────────────────────────────
  executeAiService(serviceCode: string, idempotencyKey: string, propertyId?: string, inputData?: string): Observable<AiUsageResult> {
    return this.http.post<AiUsageResult>(`${BASE}/usage/${serviceCode}`, { idempotencyKey, propertyId, inputData }, { withCredentials: true });
  }

  /** Executes LISTING_DESCRIPTION sending in-memory property images (without persisting them). */
  executeListingDescriptionUpload(
    idempotencyKey: string,
    images: File[],
    propertyId?: string,
    inputData?: string,
  ): Observable<AiUsageResult> {
    const formData = new FormData();
    formData.append('idempotencyKey', idempotencyKey);
    if (propertyId) formData.append('propertyId', propertyId);
    if (inputData) formData.append('inputData', inputData);
    for (const image of images ?? []) {
      formData.append('images', image, image.name || 'property-image.jpg');
    }

    return this.http.post<AiUsageResult>(
      `${BASE}/usage/listing-description/upload`,
      formData,
      { withCredentials: true },
    );
  }

  /** Executes DOC_INTELLIGENCE_PROPERTY with uploaded files (without persisting docs). */
  executeDocIntelligenceUpload(
    idempotencyKey: string,
    docs: File[],
    propertyId?: string,
    inputData?: string,
  ): Observable<AiUsageResult> {
    const formData = new FormData();
    formData.append('idempotencyKey', idempotencyKey);
    if (propertyId) formData.append('propertyId', propertyId);
    if (inputData) formData.append('inputData', inputData);
    for (const doc of docs ?? []) {
      formData.append('docs', doc, doc.name);
    }

    return this.http.post<AiUsageResult>(
      `${BASE}/usage/doc-intelligence-property/upload`,
      formData,
      { withCredentials: true },
    );
  }

  executeHomeStagingUpload(data: HomeStagingAiRequest): Observable<AiUsageResult> {
    const formData = new FormData();
    formData.append('idempotencyKey', data.idempotencyKey);
    if (data.propertyId) formData.append('propertyId', data.propertyId);
    formData.append(
      'inputData',
      JSON.stringify({
        roomType: data.roomType,
        style: data.style,
        quality: data.quality,
        variants: data.variants,
        customInstructions: data.customInstructions,
        imageCaption: data.imageCaption,
        propertyContext: data.propertyContext ?? {},
      }),
    );
    formData.append('image', data.image, data.imageName || 'property-image.jpg');

    return this.http.post<AiUsageResult>(
      `${BASE}/usage/home-staging-ai/upload`,
      formData,
      { withCredentials: true },
    );
  }

  executeImageEnhancementUpload(data: ImageEnhancementAiRequest): Observable<AiUsageResult> {
    const formData = new FormData();
    formData.append('idempotencyKey', data.idempotencyKey);
    if (data.propertyId) formData.append('propertyId', data.propertyId);
    formData.append(
      'inputData',
      JSON.stringify({
        enhancements: data.enhancements ?? [],
        quality: data.quality,
        customInstructions: data.customInstructions,
        imageCaption: data.imageCaption,
        propertyContext: data.propertyContext ?? {},
      }),
    );
    formData.append('image', data.image, data.imageName || 'property-image.jpg');

    return this.http.post<AiUsageResult>(
      `${BASE}/usage/image-enhancement-ai/upload`,
      formData,
      { withCredentials: true },
    );
  }

  /** Executes BACKGROUND_REMOVAL (rembg) on the uploaded image (pool-funded). */
  executeBackgroundRemovalUpload(data: BackgroundRemovalRequest): Observable<AiUsageResult> {
    const formData = new FormData();
    formData.append('idempotencyKey', data.idempotencyKey);
    formData.append('image', data.image, data.imageName || 'profile-image.jpg');

    return this.http.post<AiUsageResult>(
      `${BASE}/usage/background-removal/upload`,
      formData,
      { withCredentials: true },
    );
  }

  getUsageByIdempotency(idempotencyKey: string): Observable<AiUsageLookupResult> {
    return this.http.get<AiUsageLookupResult>(
      `${BASE}/usage/idempotency/${encodeURIComponent(idempotencyKey)}`,
      { withCredentials: true },
    );
  }

  waitForUsageResultByIdempotency(
    idempotencyKey: string,
    options?: { maxAttempts?: number; intervalMs?: number },
  ): Observable<AiUsageLookupResult> {
    const maxAttempts = Math.max(1, Number(options?.maxAttempts ?? 24));
    const intervalMs = Math.max(1000, Number(options?.intervalMs ?? 2500));

    return timer(0, intervalMs).pipe(
      switchMap((attempt) =>
        this.getUsageByIdempotency(idempotencyKey).pipe(
          map((result) => ({ attempt, result })),
        ),
      ),
      filter(({ attempt, result }) =>
        result.status !== 'PENDING' || attempt >= maxAttempts - 1,
      ),
      take(1),
      map(({ attempt, result }) => {
        if (result.status === 'PENDING' && attempt >= maxAttempts - 1) {
          return {
            ok: false,
            status: 'ERROR' as const,
            errorCode: 'USAGE_RESULT_TIMEOUT',
            errorMessage: 'Tempo limite ao aguardar o resultado da extração.',
          };
        }
        return result;
      }),
    );
  }

  /** Extract property data from uploaded documents (DOC_INTELLIGENCE_PROPERTY) */
  extractPropertyFromDocuments(documentTypes: string[]): Observable<AiUsageResult> {
    const idempotencyKey = `doc-extract-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    const inputData = JSON.stringify({ documentTypes });
    return this.executeAiService('DOC_INTELLIGENCE_PROPERTY', idempotencyKey, undefined, inputData);
  }

  getUsageLogs(params: { page?: number; pageSize?: number; userId?: string; storeId?: number; aiServiceId?: number; status?: string; dateFrom?: string; dateTo?: string }): Observable<{ items: AiUsageLog[]; total: number }> {
    let httpParams = new HttpParams()
      .set('page', params.page ?? 1)
      .set('pageSize', params.pageSize ?? 20);
    if (params.status) httpParams = httpParams.set('status', params.status);
    if (params.userId) httpParams = httpParams.set('userId', params.userId);
    if (params.storeId) httpParams = httpParams.set('storeId', params.storeId);
    if (params.aiServiceId) httpParams = httpParams.set('aiServiceId', params.aiServiceId);
    if (params.dateFrom) httpParams = httpParams.set('dateFrom', params.dateFrom);
    if (params.dateTo) httpParams = httpParams.set('dateTo', params.dateTo);
    return this.http.get<any>(`${BASE}/usage/logs`, { params: httpParams, withCredentials: true });
  }

  // ── Service credit pools (créditos exclusivos por serviço) ───────────────
  getServicePools(): Observable<AiServicePool[]> {
    return this.http.get<AiServicePool[]>(`${BASE}/pools`, { withCredentials: true });
  }

  /** MASTER-only: transfere créditos entre a carteira da entidade e o pool do serviço. */
  transferPoolCredits(data: {
    serviceCode: string;
    direction: 'TO_POOL' | 'FROM_POOL';
    amount: number;
    note?: string;
  }): Observable<any> {
    return this.http.post<any>(`${BASE}/pools/transfer`, data, { withCredentials: true });
  }

  // ── Services ──────────────────────────────────────────────────────────────
  getAiServices(): Observable<any[]> {
    return this.http.get<any[]>(`${BASE}/services`, { withCredentials: true });
  }

  /**
   * Returns all active services enriched with the entity's activation status.
   * Com `propertyId` devolve também `nextUsageCost`/`firstUseFree` por imóvel
   * (primeiro título grátis).
   */
  getServicesWithStatus(propertyId?: string): Observable<any[]> {
    let params = new HttpParams();
    if (propertyId) params = params.set('propertyId', propertyId);
    return this.http.get<any[]>(`${BASE}/services/with-status`, { params, withCredentials: true });
  }

  /** Liga ao imóvel acabado de criar os usos de IA feitos durante a criação (sem id). */
  linkUsageToProperty(propertyId: string, idempotencyKeys: string[]): Observable<{ linked: number }> {
    return this.http.post<{ linked: number }>(
      `${BASE}/usage/link-property`,
      { propertyId, idempotencyKeys },
      { withCredentials: true },
    );
  }

  /** Toggles the activation of a service for the current entity. */
  toggleService(code: string): Observable<{ activated: boolean }> {
    return this.http.patch<{ activated: boolean }>(`${BASE}/services/${code}/toggle`, {}, { withCredentials: true });
  }

  /**
   * Returns the service codes activated for the current user's entity.
   * Used to conditionally show AI features (e.g. the doc extraction wizard).
   */
  getActivatedServiceCodes(): Observable<string[]> {
    return this.http
      .get<{ codes: string[] }>(`${BASE}/services/activated`, { withCredentials: true })
      .pipe(map((res) => res.codes));
  }

  /** Price config used to preview monetary value of requested credits. */
  getBillingConfig(): Observable<AiBillingConfig> {
    return this.http.get<AiBillingConfig>(`${BASE}/services/billing-config`, { withCredentials: true });
  }

  // ══════════════════════════════════════════════════════════════════════════════
  // Admin-only methods (support user)
  // ══════════════════════════════════════════════════════════════════════════════

  // Providers
  adminGetProviders(): Observable<any[]> {
    return this.http.get<any[]>(`${BASE}/admin/providers`, { withCredentials: true });
  }

  adminCreateProvider(data: { code: string; name: string; description?: string; baseUrl?: string; apiKeyRef?: string; status: boolean }): Observable<any> {
    return this.http.post<any>(`${BASE}/admin/providers`, data, { withCredentials: true });
  }

  adminUpdateProvider(id: number, data: Partial<{ code: string; name: string; description: string; baseUrl: string; apiKeyRef: string; status: boolean }>): Observable<any> {
    return this.http.patch<any>(`${BASE}/admin/providers/${id}`, data, { withCredentials: true });
  }

  adminDeleteProvider(id: number): Observable<any> {
    return this.http.delete<any>(`${BASE}/admin/providers/${id}`, { withCredentials: true });
  }

  // Services (admin full CRUD)
  adminGetServices(): Observable<any[]> {
    return this.http.get<any[]>(`${BASE}/admin/services`, { withCredentials: true });
  }

  adminCreateService(data: { code: string; name: string; description?: string; defaultCreditCost: number; providerId: number; status: boolean; metadata?: AiServiceMetadata }): Observable<any> {
    return this.http.post<any>(`${BASE}/admin/services`, data, { withCredentials: true });
  }

  adminUpdateService(id: number, data: Partial<{ code: string; name: string; description: string; defaultCreditCost: number; providerId: number; status: boolean; metadata: AiServiceMetadata }>): Observable<any> {
    return this.http.patch<any>(`${BASE}/admin/services/${id}`, data, { withCredentials: true });
  }

  adminDeleteService(id: number): Observable<any> {
    return this.http.delete<any>(`${BASE}/admin/services/${id}`, { withCredentials: true });
  }

  // Credit Requests (global admin view)
  private buildAdminCreditRequestParams(filters: AiCreditRequestFilters): HttpParams {
    let params = new HttpParams();
    if (filters?.status) params = params.set('status', filters.status);
    if (filters?.origin) params = params.set('origin', filters.origin);
    if (filters?.entityId) params = params.set('entityId', String(filters.entityId));
    if (filters?.storeId) params = params.set('storeId', String(filters.storeId));
    if (filters?.from) params = params.set('from', filters.from);
    if (filters?.to) params = params.set('to', filters.to);
    return params;
  }

  adminGetAllCreditRequests(
    page = 1,
    pageSize = 10,
    filters: AiCreditRequestFilters = {},
  ): Observable<{ items: AiCreditRequest[]; total: number }> {
    const params = this.buildAdminCreditRequestParams(filters).set('page', page).set('pageSize', pageSize);
    return this.http.get<any>(`${BASE}/admin/credit-requests`, { params, withCredentials: true });
  }

  /** Excel com todas as páginas dos filtros atuais (o erro do servidor chega como Blob). */
  adminExportCreditRequests(filters: AiCreditRequestFilters): Observable<Blob> {
    const params = this.buildAdminCreditRequestParams(filters);
    return this.http.get(`${BASE}/admin/credit-requests/export/excel`, {
      params,
      responseType: 'blob',
      withCredentials: true,
    });
  }

  /** Opções de entidade/agência do admin de faturação: mesmo guard de support user, só ativas. */
  adminGetEntityOptions(): Observable<AdminOption[]> {
    return this.http.get<AdminOption[]>(`${environment.apiUrl}/fiscal-bridge/admin/entity-options`, { withCredentials: true });
  }

  adminGetStoreOptions(entityId: number): Observable<AdminOption[]> {
    return this.http.get<AdminOption[]>(`${environment.apiUrl}/fiscal-bridge/admin/store-options/${entityId}`, { withCredentials: true });
  }

  adminDecideCreditRequest(id: number, status: string, decisionNote?: string): Observable<AiCreditRequest> {
    return this.http.patch<AiCreditRequest>(`${BASE}/admin/credit-requests/${id}/decide`, { status, decisionNote }, { withCredentials: true });
  }

  // Direct wallet grant
  adminGrantCredits(data: { entityId: number; storeId?: number; userId?: string; credits: number; note?: string; allStores?: boolean }): Observable<AiAdminGrantResult> {
    return this.http.post<AiAdminGrantResult>(`${BASE}/admin/grant`, data, { withCredentials: true });
  }

  adminGetGrantPreview(params: { entityId: number; scope: AiAdminGrantScope; storeId?: number | null; userId?: string | null }): Observable<AiAdminGrantPreview> {
    let httpParams = new HttpParams().set('entityId', String(params.entityId)).set('scope', params.scope);
    if (params.storeId) httpParams = httpParams.set('storeId', String(params.storeId));
    if (params.userId) httpParams = httpParams.set('userId', params.userId);
    return this.http.get<AiAdminGrantPreview>(`${BASE}/admin/grant/preview`, { params: httpParams, withCredentials: true });
  }

  adminGetEntityUsers(entityId: number, search?: string): Observable<AdminUserOption[]> {
    let params = new HttpParams();
    if (search) params = params.set('search', search);
    return this.http.get<AdminUserOption[]>(`${BASE}/admin/entity-users/${entityId}`, { params, withCredentials: true });
  }

  adminGetBillingConfig(): Observable<AiBillingConfig> {
    return this.http.get<AiBillingConfig>(`${BASE}/admin/billing-config`, { withCredentials: true });
  }

  adminUpdateBillingConfig(data: Partial<AiBillingConfig>): Observable<AiBillingConfig> {
    return this.http.patch<AiBillingConfig>(`${BASE}/admin/billing-config`, data, { withCredentials: true });
  }

  // Model prices + cost report (custo real)
  adminGetModelPrices(): Observable<AiModelPrice[]> {
    return this.http.get<AiModelPrice[]>(`${BASE}/admin/model-prices`, { withCredentials: true });
  }

  adminCreateModelPrice(data: Partial<AiModelPrice>): Observable<AiModelPrice> {
    return this.http.post<AiModelPrice>(`${BASE}/admin/model-prices`, data, { withCredentials: true });
  }

  adminUpdateModelPrice(id: number, data: Partial<AiModelPrice>): Observable<AiModelPrice> {
    return this.http.patch<AiModelPrice>(`${BASE}/admin/model-prices/${id}`, data, { withCredentials: true });
  }

  adminDeleteModelPrice(id: number): Observable<any> {
    return this.http.delete<any>(`${BASE}/admin/model-prices/${id}`, { withCredentials: true });
  }

  adminGetCostUsages(filters: AiCostReportFilters, page = 1, pageSize = 20): Observable<AiCostUsagesPage> {
    let params = new HttpParams().set('page', page).set('pageSize', pageSize);
    if (filters?.from) params = params.set('from', filters.from);
    if (filters?.to) params = params.set('to', filters.to);
    if (filters?.entityId) params = params.set('entityId', String(filters.entityId));
    if (filters?.aiServiceId) params = params.set('aiServiceId', String(filters.aiServiceId));
    return this.http.get<AiCostUsagesPage>(`${BASE}/admin/costs/usages`, { params, withCredentials: true });
  }

  adminGetCostReport(filters: AiCostReportFilters): Observable<AiCostReport> {
    let params = new HttpParams();
    if (filters?.from) params = params.set('from', filters.from);
    if (filters?.to) params = params.set('to', filters.to);
    if (filters?.entityId) params = params.set('entityId', String(filters.entityId));
    if (filters?.aiServiceId) params = params.set('aiServiceId', String(filters.aiServiceId));
    if (filters?.groupBy) params = params.set('groupBy', filters.groupBy);
    return this.http.get<AiCostReport>(`${BASE}/admin/costs`, { params, withCredentials: true });
  }

  // ══════════════════════════════════════════════════════════════════════════════
  // MASTER-only: AI credit policies (entity / store / wallet)
  // ══════════════════════════════════════════════════════════════════════════════

  getPoliciesOverview(): Observable<AiCreditPolicyOverview> {
    return this.http.get<AiCreditPolicyOverview>(`${BASE}/policies`, { withCredentials: true });
  }

  updateEntityPolicy(data: {
    storeCanRequestOwnCredits?: boolean;
    userCanRequestOwnCredits?: boolean;
    creditRequestFlow?: 'MASTER_APPROVAL_REQUIRED' | 'DIRECT_TO_IMODIGI';
    invoiceRecipientForStoreRequests?: 'ENTITY' | 'STORE';
    invoiceRecipientForUserRequests?: 'ENTITY' | 'STORE' | 'USER';
  }): Observable<any> {
    return this.http.patch<any>(`${BASE}/policies/entity`, data, { withCredentials: true });
  }

  updateStorePolicy(storeId: number, data: {
    userCanRequestOwnCredits?: boolean;
    invoiceRecipientForUserRequests?: 'ENTITY' | 'STORE' | 'USER' | null;
  }): Observable<any> {
    return this.http.patch<any>(`${BASE}/policies/stores/${storeId}`, data, { withCredentials: true });
  }

  updateWalletShare(walletId: number, shareWithSubordinates: boolean): Observable<any> {
    return this.http.patch<any>(`${BASE}/policies/wallets/${walletId}/share`, { shareWithSubordinates }, { withCredentials: true });
  }
}
